import Phaser from '../lib/phaser.js';
import { SCENE_KEYS } from './scene-keys.js';
import { KENNEY_FUTURE_NARROW_FONT_NAME } from '../assets/font-keys.js';
import { BaseScene } from './base-scene.js';
import { UI, makeCloseButton } from '../utils/ui-button.js';
import { netManager } from '../net/index.js';
import { showRewardToast } from '../net/reward-toast.js';
import {
  apiConfigured,
  fetchTokenConfig,
  fetchTokenBalance,
  fetchMintableBeasts,
  mintBeast,
  fetchOwnedBeasts,
  fetchStakes,
  lockStake,
  unlockStake,
  claimStake,
} from '../net/api.js';
import { canPayToken, payTokenToTreasury } from '../net/token-pay.js';

// ── The Vault — "$TOKEN-powered on-chain beasts". ──
// One overlay scene, three tabs:
//   • MINT  — turn a caught beast into a compressed NFT (cNFT) the player owns, for a small
//             $TOKEN fee. The client signs+sends the fee transfer, the server verifies + mints.
//   • STAKE — lock owned beast-cNFTs to accrue $TOKEN over time; claim pays out a real transfer.
//   • INFO  — plain explanation of how it works.
// A live $TOKEN balance pill sits in the header (mirrors the Gold chip elsewhere). When the API
// says the token feature is NOT configured (or we're offline), the whole panel degrades to a calm
// "coming soon" state — no buttons, no crash. House style: cozy pixel-RPG warm-wood UI helpers.

// Warm-wood DOM palette (mirrors utils/ui-button.js UI.* so the DOM lists match the Phaser chrome).
const D = Object.freeze({
  text: '#f4ead2',
  sub: '#bda886',
  gold: '#e0b15a',
  emerald: '#86c06a',
  danger: '#e08a6a',
  panel: 'rgba(42,32,24,0.97)',
  panelSoft: 'rgba(32,24,18,0.92)',
  stroke: 'rgba(107,84,54,0.85)',
  strokeSoft: 'rgba(107,84,54,0.45)',
});

/**
 * @typedef VaultSceneData
 * @type {object}
 * @property {string} [previousSceneName]
 */

export class VaultScene extends BaseScene {
  /** @type {VaultSceneData} */ #sceneData;
  /** @type {'mint' | 'stake' | 'info'} */ #tab = 'mint';
  /** @type {import('@mn/shared').TokenConfigResponse | null} */ #config = null;
  /** @type {number} */ #balance = 0;
  /** @type {boolean} guard re-entrant close */ #closing = false;
  /** @type {HTMLElement[]} live DOM nodes (cleaned up on shutdown) */ #domEls = [];
  /** @type {Phaser.GameObjects.Text | undefined} */ #balanceText;
  /** @type {Phaser.GameObjects.Text | undefined} */ #statusText;
  /** @type {{txt:Phaser.GameObjects.Text, underline:Phaser.GameObjects.Rectangle, key:string}[]} */ #tabs = [];
  /** @type {{left:number, top:number, w:number, h:number}} */ #panel = { left: 0, top: 0, w: 0, h: 0 };

  constructor() {
    super({ key: SCENE_KEYS.VAULT_SCENE });
  }

  /** @param {VaultSceneData} data */
  init(data) {
    super.init(data);
    this.#sceneData = data || {};
    this.#tab = 'mint';
    this.#config = null;
    this.#balance = 0;
    this.#closing = false;
    this.#domEls = [];
    this.#tabs = [];
  }

  create() {
    super.create();
    this._claimOverlayInput();
    const { width, height } = this.scale;

    // dark scrim
    this.add.rectangle(0, 0, width, height, 0x05080d, 0.82).setOrigin(0);

    const panelW = Math.min(900, width - 40);
    const panelH = Math.min(640, height - 40);
    const left = width / 2 - panelW / 2;
    const top = height / 2 - panelH / 2;
    this.#panel = { left, top, w: panelW, h: panelH };

    // wooden panel
    const g = this.add.graphics();
    g.fillStyle(UI.glass, 0.97);
    g.fillRoundedRect(left, top, panelW, panelH, 16);
    g.lineStyle(3, UI.ink, 1);
    g.strokeRoundedRect(left, top, panelW, panelH, 16);
    g.lineStyle(1.5, UI.glassStroke, 1);
    g.strokeRoundedRect(left + 2.5, top + 2.5, panelW - 5, panelH - 5, 14);

    // title
    this.add
      .text(left + 28, top + 30, '🔒 VAULT', {
        fontFamily: KENNEY_FUTURE_NARROW_FONT_NAME,
        fontSize: '30px',
        color: UI.emeraldHex,
        stroke: '#0c2a14',
        strokeThickness: 6,
      })
      .setOrigin(0, 0.5);

    // $TOKEN balance pill (top-right, before the close button) — mirrors the Gold chip elsewhere
    this.#buildBalancePill(left + panelW - 70, top + 30);

    makeCloseButton(this, left + panelW - 30, top + 30, () => this.#close());

    // tabs row
    this.#buildTabs(left + 28, top + 72);

    // centered status / loading / coming-soon text
    this.#statusText = this.add
      .text(width / 2, top + panelH / 2, 'Loading…', {
        fontFamily: KENNEY_FUTURE_NARROW_FONT_NAME,
        fontSize: '20px',
        color: UI.subHex,
        align: 'center',
        wordWrap: { width: panelW - 120 },
      })
      .setOrigin(0.5);

    this.cameras.main.fadeIn(160, 0, 0, 0);
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => this.#destroyDom());

    void this.#boot();
  }

  /** Load config + balance, then render the active tab (or the coming-soon state). */
  async #boot() {
    if (!apiConfigured()) {
      this.#showComingSoon();
      return;
    }
    this.#setStatus('Loading…');
    const [config] = await Promise.all([fetchTokenConfig(), this.#refreshBalance()]);
    if (!this.sys.isActive()) return;
    this.#config = config;
    if (!config.enabled) {
      this.#showComingSoon();
      return;
    }
    this.#renderTab();
  }

  // ── header bits ──

  #buildBalancePill(rightX, y) {
    const pill = this.add.container(rightX, y);
    const labelW = 132;
    const bg = this.add.graphics();
    bg.fillStyle(0x1c1610, 0.97);
    bg.fillRoundedRect(-labelW, -16, labelW, 32, 8);
    bg.lineStyle(2, UI.gold, 0.85);
    bg.strokeRoundedRect(-labelW, -16, labelW, 32, 8);
    const tag = this.add
      .text(-labelW + 12, 0, '$TOKEN', { fontFamily: KENNEY_FUTURE_NARROW_FONT_NAME, fontSize: '13px', color: UI.goldHex })
      .setOrigin(0, 0.5);
    this.#balanceText = this.add
      .text(-10, 0, '0', { fontFamily: KENNEY_FUTURE_NARROW_FONT_NAME, fontSize: '16px', color: UI.goldHex })
      .setOrigin(1, 0.5);
    pill.add([bg, tag, this.#balanceText]);
    // anchor so its right edge lands at rightX
    pill.setX(rightX);
  }

  async #refreshBalance() {
    const wallet = netManager.wallet;
    this.#balance = await fetchTokenBalance(wallet);
    this.#balanceText?.setText(this.#fmt(this.#balance));
  }

  #buildTabs(x, y) {
    const defs = [
      { key: 'mint', label: 'MINT' },
      { key: 'stake', label: 'STAKE' },
      { key: 'info', label: 'INFO' },
    ];
    let cx = x;
    for (const d of defs) {
      const active = this.#tab === d.key;
      const txt = this.add
        .text(cx, y, d.label, {
          fontFamily: KENNEY_FUTURE_NARROW_FONT_NAME,
          fontSize: '20px',
          color: active ? UI.goldHex : UI.subHex,
        })
        .setOrigin(0, 0.5)
        .setInteractive({ useHandCursor: true });
      const underline = this.add
        .rectangle(cx, y + 16, txt.width, 3, UI.gold, active ? 1 : 0)
        .setOrigin(0, 0.5);
      txt.on('pointerover', () => txt.setColor(UI.goldHex));
      txt.on('pointerout', () => txt.setColor(this.#tab === d.key ? UI.goldHex : UI.subHex));
      txt.on('pointerdown', () => this.#switchTab(d.key));
      this.#tabs.push({ txt, underline, key: d.key });
      cx += txt.width + 36;
    }
  }

  #switchTab(key) {
    if (this.#tab === key) return;
    this.#tab = key;
    this.#tabs.forEach((t) => {
      const active = t.key === key;
      t.txt.setColor(active ? UI.goldHex : UI.subHex);
      t.underline.setAlpha(active ? 1 : 0);
      t.underline.width = t.txt.width;
    });
    if (this.#config?.enabled) this.#renderTab();
  }

  #renderTab() {
    this.#destroyDom();
    if (this.#tab === 'mint') void this.#renderMint();
    else if (this.#tab === 'stake') void this.#renderStake();
    else this.#renderInfo();
  }

  // ── coming-soon / status ──

  #showComingSoon() {
    this.#destroyDom();
    this.#tabs.forEach((t) => {
      t.txt.setVisible(false);
      t.underline.setVisible(false);
    });
    this.#setStatus(
      'On-chain beasts — coming soon\n\n' +
        'Mint your beasts as tradeable NFTs, stake them to\n' +
        'earn $TOKEN, and spend $TOKEN in the shop.\n' +
        'Launching with $TOKEN.',
    );
  }

  #setStatus(msg) {
    this.#statusText?.setText(msg).setVisible(true);
  }
  #hideStatus() {
    this.#statusText?.setVisible(false);
  }

  // ── MINT tab ──

  async #renderMint() {
    const wallet = netManager.wallet;
    if (!wallet) {
      this.#setStatus('Connect your wallet to mint your beasts on-chain.');
      return;
    }
    this.#setStatus('Loading your beasts…');
    const beasts = await fetchMintableBeasts(wallet);
    if (!this.sys.isActive() || this.#tab !== 'mint') return;

    if (beasts.length === 0) {
      this.#setStatus('No beasts to mint yet. Catch some, then immortalize them on-chain.');
      return;
    }
    this.#hideStatus();

    const scroll = this.#scrollList();
    for (const b of beasts) scroll.appendChild(this.#mintRow(b));
  }

  /** @param {import('@mn/shared').MintableBeast} b */
  #mintRow(b) {
    const row = this.#rowShell();

    const info = document.createElement('div');
    info.style.cssText = 'flex:1;display:flex;flex-direction:column;gap:3px;min-width:0;';
    info.innerHTML =
      `<div style="color:${D.text};font:600 15px system-ui;overflow:hidden;text-overflow:ellipsis;white-space:nowrap">${this.#esc(b.name)}</div>` +
      `<div style="color:${D.sub};font:500 12px system-ui">Lv ${b.level} · ${this.#esc(b.element)} · ${this.#esc(b.tier)}</div>`;
    row.appendChild(info);

    if (b.minted) {
      const badge = document.createElement('div');
      badge.textContent = '⛓ On-chain';
      badge.style.cssText = `flex-shrink:0;padding:6px 12px;border-radius:8px;background:rgba(134,192,106,0.14);border:1px solid ${D.emerald};color:${D.emerald};font:600 13px system-ui;`;
      row.appendChild(badge);
      return row;
    }

    const btn = document.createElement('button');
    btn.type = 'button';
    btn.textContent = `MINT · ${this.#fmt(b.feeToken)} $TOKEN`;
    btn.style.cssText = this.#primaryBtnCss();
    btn.onmouseenter = () => (btn.style.background = '#9bd07f');
    btn.onmouseleave = () => (btn.style.background = D.emerald);
    btn.onclick = () => this.#openMintConfirm(b, btn);
    row.appendChild(btn);
    return row;
  }

  /**
   * @param {import('@mn/shared').MintableBeast} b
   * @param {HTMLButtonElement} sourceBtn the row button (re-enabled on cancel/failure)
   */
  #openMintConfirm(b, sourceBtn) {
    const overlay = this.#modalOverlay();
    const panel = this.#modalPanel(overlay, 460);

    panel.appendChild(this.#h2('Mint on-chain'));
    panel.appendChild(
      this.#note(
        `Turn ${this.#esc(b.name)} (Lv ${b.level}, ${this.#esc(b.tier)}) into a tradeable cNFT in your wallet.`,
        D.sub,
      ),
    );

    // metadata preview — the exact traits the cNFT will carry (server is authoritative).
    const attrs = document.createElement('div');
    attrs.style.cssText = 'display:grid;grid-template-columns:1fr 1fr;gap:6px 14px;margin:10px 0;';
    const traits = [
      ['Species', b.name],
      ['Element', b.element],
      ['Level', String(b.level)],
      ['Rarity', this.#cap(b.tier)],
    ];
    for (const [k, v] of traits) {
      const cell = document.createElement('div');
      cell.innerHTML = `<span style="color:${D.sub};font:500 12px system-ui">${this.#esc(k)}</span> <span style="color:${D.text};font:600 13px system-ui;float:right">${this.#esc(v)}</span>`;
      attrs.appendChild(cell);
    }
    panel.appendChild(attrs);

    panel.appendChild(this.#feeLine(b.feeToken));
    const err = this.#note('', D.danger);
    panel.appendChild(err);

    const cancel = () => {
      this.#closeOverlay(overlay);
      sourceBtn.disabled = false;
      sourceBtn.style.opacity = '1';
    };
    let busy = false;
    const submit = { label: 'Confirm & Pay', primary: true, onClick: () => {} };
    submit.onClick = async () => {
      if (busy) return;
      if (this.#balance < b.feeToken) {
        err.textContent = `Not enough $TOKEN. You need ${this.#fmt(b.feeToken)}.`;
        return;
      }
      if (!canPayToken()) {
        err.textContent = 'Your wallet cannot sign transactions here. Open in a wallet browser.';
        return;
      }
      busy = true;
      sourceBtn.disabled = true;
      sourceBtn.style.opacity = '0.6';
      err.style.color = D.sub;
      err.textContent = 'Approve the $TOKEN fee in your wallet…';
      const sig = await this.#payFee(b.feeToken);
      if (!sig) {
        busy = false;
        err.style.color = D.danger;
        err.textContent = 'Payment was not completed. Please try again.';
        sourceBtn.disabled = false;
        sourceBtn.style.opacity = '1';
        return;
      }
      err.textContent = 'Minting your beast on-chain…';
      const res = await mintBeast(netManager.wallet, b.beastUid, sig);
      busy = false;
      if (res.ok && res.assetId) {
        this.#closeOverlay(overlay);
        await this.#refreshBalance();
        if (this.sys.isActive()) {
          showRewardToast(this, `${b.name} is now on-chain!`, b.tier);
          if (this.#tab === 'mint') void this.#renderMint();
        }
        return;
      }
      err.style.color = D.danger;
      err.textContent =
        res.reason === 'already_minted'
          ? 'This beast is already minted.'
          : 'Mint failed — your fee is safe. Please try again.';
      sourceBtn.disabled = false;
      sourceBtn.style.opacity = '1';
    };

    panel.appendChild(this.#modalButtons(overlay, [{ label: 'Cancel', onClick: cancel }, submit]));
  }

  // ── STAKE tab ──

  async #renderStake() {
    const wallet = netManager.wallet;
    if (!wallet) {
      this.#setStatus('Connect your wallet to stake your on-chain beasts.');
      return;
    }
    this.#setStatus('Loading your staked beasts…');
    const [owned, stakeList] = await Promise.all([fetchOwnedBeasts(wallet), fetchStakes(wallet)]);
    if (!this.sys.isActive() || this.#tab !== 'stake') return;
    this.#hideStatus();

    const container = this.#fullPanelDom();
    container.style.display = 'flex';
    container.style.flexDirection = 'column';
    container.style.gap = '12px';

    // claim bar
    const claimBar = document.createElement('div');
    claimBar.style.cssText = `display:flex;align-items:center;justify-content:space-between;gap:12px;padding:12px 14px;border-radius:12px;background:${D.panelSoft};border:1px solid ${D.stroke};`;
    const claimInfo = document.createElement('div');
    claimInfo.innerHTML =
      `<div style="color:${D.gold};font:700 18px system-ui">${this.#fmt(stakeList.pendingToken)} $TOKEN</div>` +
      `<div style="color:${D.sub};font:500 12px system-ui">${stakeList.poolPaused ? 'Reward pool empty — accrual paused' : 'Claimable now'}</div>`;
    const claimBtn = document.createElement('button');
    claimBtn.type = 'button';
    claimBtn.textContent = 'CLAIM';
    claimBtn.style.cssText = this.#primaryBtnCss();
    const canClaim = stakeList.pendingToken > 0;
    if (!canClaim) {
      claimBtn.disabled = true;
      claimBtn.style.opacity = '0.5';
      claimBtn.style.cursor = 'default';
    } else {
      claimBtn.onmouseenter = () => (claimBtn.style.background = '#9bd07f');
      claimBtn.onmouseleave = () => (claimBtn.style.background = D.emerald);
      claimBtn.onclick = () => this.#doClaim(claimBtn, claimInfo);
    }
    claimBar.append(claimInfo, claimBtn);
    container.appendChild(claimBar);

    // two scrollable columns: Owned (stakeable) | Staked
    const cols = document.createElement('div');
    cols.style.cssText = 'flex:1;display:flex;gap:12px;min-height:0;';

    const ownedCol = this.#stakeColumn('Your beast-NFTs');
    const stakeable = owned.filter((o) => !o.staked);
    if (stakeable.length === 0) {
      ownedCol.body.appendChild(this.#emptyHint('Mint a beast first, then stake it here to earn.'));
    } else {
      for (const o of stakeable) ownedCol.body.appendChild(this.#ownedRow(o));
    }

    const stakedCol = this.#stakeColumn('Staked · earning');
    if (stakeList.staked.length === 0) {
      stakedCol.body.appendChild(this.#emptyHint('Nothing staked yet.'));
    } else {
      for (const s of stakeList.staked) stakedCol.body.appendChild(this.#stakedRow(s));
    }

    cols.append(ownedCol.wrap, stakedCol.wrap);
    container.appendChild(cols);
  }

  /** @param {import('@mn/shared').OwnedBeastCnft} o */
  #ownedRow(o) {
    const row = this.#rowShell();
    const info = document.createElement('div');
    info.style.cssText = 'flex:1;min-width:0;';
    const lvl = o.level != null ? `Lv ${o.level}` : 'External';
    const tier = o.tier ? ` · ${this.#cap(o.tier)}` : '';
    info.innerHTML =
      `<div style="color:${D.text};font:600 14px system-ui;overflow:hidden;text-overflow:ellipsis;white-space:nowrap">${this.#esc(o.name)}</div>` +
      `<div style="color:${D.sub};font:500 11px system-ui">${lvl}${tier}</div>`;
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.textContent = 'STAKE';
    btn.style.cssText = this.#smallBtnCss();
    btn.onclick = async () => {
      btn.disabled = true;
      btn.style.opacity = '0.6';
      const res = await lockStake(netManager.wallet, o.assetId);
      if (res.ok) {
        if (this.#tab === 'stake') void this.#renderStake();
      } else {
        btn.disabled = false;
        btn.style.opacity = '1';
        btn.textContent = res.reason === 'max_staked' ? 'MAX' : 'RETRY';
      }
    };
    row.append(info, btn);
    return row;
  }

  /** @param {import('@mn/shared').StakedBeast} s */
  #stakedRow(s) {
    const row = this.#rowShell();
    const info = document.createElement('div');
    info.style.cssText = 'flex:1;min-width:0;';
    info.innerHTML =
      `<div style="color:${D.text};font:600 14px system-ui;overflow:hidden;text-overflow:ellipsis;white-space:nowrap">${this.#esc(s.name)}</div>` +
      `<div style="color:${D.emerald};font:500 11px system-ui">+${this.#fmt(s.ratePerDay)}/day · ${this.#fmt(s.accruedToken)} earned</div>`;
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.textContent = 'UNSTAKE';
    btn.style.cssText = this.#smallBtnCss(true);
    if (!s.unlockable) {
      btn.disabled = true;
      btn.style.opacity = '0.5';
      btn.style.cursor = 'default';
      btn.title = 'Locked for the minimum staking window.';
    } else {
      btn.onclick = async () => {
        btn.disabled = true;
        btn.style.opacity = '0.6';
        const res = await unlockStake(netManager.wallet, s.assetId);
        if (res.ok) {
          await this.#refreshBalance();
          if (this.#tab === 'stake') void this.#renderStake();
        } else {
          btn.disabled = false;
          btn.style.opacity = '1';
          btn.textContent = 'RETRY';
        }
      };
    }
    row.append(info, btn);
    return row;
  }

  async #doClaim(btn, claimInfo) {
    btn.disabled = true;
    btn.style.opacity = '0.6';
    btn.textContent = 'CLAIMING…';
    const res = await claimStake(netManager.wallet);
    if (!this.sys.isActive()) return;
    if (res.ok && res.paidToken > 0) {
      await this.#refreshBalance();
      showRewardToast(this, `Claimed ${this.#fmt(res.paidToken)} $TOKEN!`, 'rare');
      if (this.#tab === 'stake') void this.#renderStake();
      return;
    }
    btn.disabled = false;
    btn.style.opacity = '1';
    btn.textContent = 'CLAIM';
    const sub = claimInfo.querySelector('div:last-child');
    if (sub) {
      sub.textContent =
        res.reason === 'cooldown' ? 'On cooldown — try again later.' : 'Could not claim. Try again.';
    }
  }

  // ── INFO tab ──

  #renderInfo() {
    this.#hideStatus();
    const box = this.#fullPanelDom();
    box.style.overflowY = 'auto';
    box.style.padding = '6px 10px';
    box.innerHTML =
      `<div style="color:${D.gold};font:700 18px system-ui;margin-bottom:10px">How the Vault works</div>` +
      this.#infoBlock('⛓ Mint', 'Pay a small $TOKEN fee to turn a caught beast into a compressed NFT (cNFT) you own in your wallet. Trade it on Tensor or Magic Eden — its species, level and stats travel with it.') +
      this.#infoBlock('🌱 Stake', 'Lock your beast-NFTs to earn $TOKEN over time. Rarer, higher-level beasts earn more. Staking is off-chain — keep the cNFT in your wallet to keep earning, and claim your $TOKEN whenever you like.') +
      this.#infoBlock('🛒 Spend', 'Use $TOKEN alongside Gold in the Card Shop and for premium items like better balls and daily spins.') +
      `<div style="color:${D.sub};font:500 12px system-ui;margin-top:8px">Rewards come from a fixed, pre-allocated pool — no infinite emission.</div>`;
  }

  #infoBlock(title, body) {
    return (
      `<div style="margin-bottom:12px">` +
      `<div style="color:${D.text};font:600 15px system-ui;margin-bottom:3px">${title}</div>` +
      `<div style="color:${D.sub};font:500 13px system-ui;line-height:1.45">${body}</div>` +
      `</div>`
    );
  }

  // ── payment ──

  /** @param {number} uiAmount @returns {Promise<string|null>} */
  async #payFee(uiAmount) {
    const cfg = this.#config;
    if (!cfg?.enabled) return null;
    const rpcUrl = import.meta.env.VITE_RPC_URL || '';
    return payTokenToTreasury(cfg, uiAmount, rpcUrl);
  }

  // ── DOM helpers (warm-wood, mirror card-shop's structure) ──

  /** A fixed scroll container filling the panel interior (single-column list). */
  #scrollList() {
    const rect = this.#panelDomRect();
    const scroll = document.createElement('div');
    scroll.style.cssText = [
      'position:fixed',
      `left:${rect.left}px`,
      `top:${rect.top}px`,
      `width:${rect.width}px`,
      `height:${rect.height}px`,
      'overflow-y:auto',
      'overflow-x:hidden',
      'display:flex',
      'flex-direction:column',
      'gap:10px',
      'padding:6px 10px',
      'box-sizing:border-box',
      'z-index:60',
      'scrollbar-width:thin',
    ].join(';');
    document.body.appendChild(scroll);
    this.#domEls.push(scroll);
    return scroll;
  }

  /** A fixed container filling the panel interior (for multi-region tabs like Stake/Info). */
  #fullPanelDom() {
    const rect = this.#panelDomRect();
    const box = document.createElement('div');
    box.style.cssText = [
      'position:fixed',
      `left:${rect.left}px`,
      `top:${rect.top}px`,
      `width:${rect.width}px`,
      `height:${rect.height}px`,
      'box-sizing:border-box',
      'z-index:60',
    ].join(';');
    document.body.appendChild(box);
    this.#domEls.push(box);
    return box;
  }

  #stakeColumn(title) {
    const wrap = document.createElement('div');
    wrap.style.cssText = `flex:1;min-width:0;display:flex;flex-direction:column;gap:8px;`;
    const head = document.createElement('div');
    head.textContent = title;
    head.style.cssText = `color:${D.gold};font:600 13px system-ui;`;
    const body = document.createElement('div');
    body.style.cssText = 'flex:1;min-height:0;overflow-y:auto;display:flex;flex-direction:column;gap:8px;scrollbar-width:thin;padding-right:2px;';
    wrap.append(head, body);
    return { wrap, body };
  }

  #rowShell() {
    const row = document.createElement('div');
    row.style.cssText = `display:flex;align-items:center;gap:12px;padding:10px 12px;border-radius:10px;background:${D.panelSoft};border:1px solid ${D.strokeSoft};`;
    return row;
  }

  #emptyHint(text) {
    const e = document.createElement('div');
    e.textContent = text;
    e.style.cssText = `color:${D.sub};font:500 12px system-ui;padding:14px 8px;text-align:center;`;
    return e;
  }

  #feeLine(fee) {
    const e = document.createElement('div');
    e.style.cssText = `display:flex;align-items:center;justify-content:space-between;margin:10px 0;padding:10px 12px;border-radius:10px;background:rgba(28,22,16,0.9);border:1px solid ${D.stroke};`;
    e.innerHTML =
      `<span style="color:${D.sub};font:500 13px system-ui">Mint fee</span>` +
      `<span style="color:${D.gold};font:700 17px system-ui">${this.#fmt(fee)} $TOKEN</span>`;
    return e;
  }

  #primaryBtnCss() {
    return `flex-shrink:0;padding:9px 16px;border-radius:9px;border:none;background:${D.emerald};color:#0c2a14;font:700 14px system-ui;cursor:pointer;`;
  }
  #smallBtnCss(secondary = false) {
    return secondary
      ? `flex-shrink:0;padding:7px 12px;border-radius:8px;border:1px solid ${D.stroke};background:transparent;color:${D.sub};font:600 12px system-ui;cursor:pointer;`
      : `flex-shrink:0;padding:7px 14px;border-radius:8px;border:none;background:${D.emerald};color:#0c2a14;font:700 12px system-ui;cursor:pointer;`;
  }

  #modalOverlay() {
    const o = document.createElement('div');
    o.style.cssText =
      'position:fixed;inset:0;background:rgba(8,5,3,0.8);backdrop-filter:blur(3px);display:flex;align-items:center;justify-content:center;z-index:120;';
    document.body.appendChild(o);
    this.#domEls.push(o);
    return o;
  }
  #modalPanel(overlay, maxWidth) {
    const p = document.createElement('div');
    p.style.cssText = [
      `width:min(${maxWidth}px,92vw)`,
      'max-height:88vh',
      'overflow-y:auto',
      'box-sizing:border-box',
      'padding:22px',
      'border-radius:16px',
      `background:${D.panel}`,
      `border:2px solid ${D.stroke}`,
      'box-shadow:0 0 32px rgba(0,0,0,0.5)',
    ].join(';');
    overlay.appendChild(p);
    return p;
  }
  #closeOverlay(overlay) {
    overlay.remove();
    this.#domEls = this.#domEls.filter((e) => e !== overlay);
  }
  #h2(t) {
    const e = document.createElement('div');
    e.textContent = t;
    e.style.cssText = `color:${D.emerald};font:700 22px system-ui;text-align:center;margin-bottom:10px;`;
    return e;
  }
  #note(t, color) {
    const e = document.createElement('div');
    e.textContent = t;
    e.style.cssText = `color:${color};font:500 13px system-ui;text-align:center;margin:6px 0;min-height:16px;line-height:1.4;`;
    return e;
  }
  /** @param {HTMLElement} overlay @param {{label:string,primary?:boolean,onClick:()=>void}[]} buttons */
  #modalButtons(overlay, buttons) {
    const row = document.createElement('div');
    row.style.cssText = 'display:flex;gap:10px;margin-top:14px;justify-content:center;flex-wrap:wrap;';
    for (const b of buttons) {
      const btn = document.createElement('button');
      btn.textContent = b.label;
      btn.style.cssText = b.primary
        ? `flex:1;min-width:140px;padding:11px 18px;border-radius:10px;border:none;background:${D.emerald};color:#0c2a14;font:700 15px system-ui;cursor:pointer;`
        : `padding:11px 18px;border-radius:10px;border:1px solid ${D.stroke};background:transparent;color:${D.sub};font:600 15px system-ui;cursor:pointer;`;
      btn.onclick = () => b.onClick();
      row.appendChild(btn);
    }
    return row;
  }

  /** Compute the DOM rect (screen px) of the panel interior, inset below the tabs row. */
  #panelDomRect() {
    const canvas = this.game.canvas;
    const bounds = canvas.getBoundingClientRect();
    const sx = bounds.width / this.scale.width;
    const sy = bounds.height / this.scale.height;
    const inX = 24;
    const inTop = 100; // below title + tabs
    const inBottom = 24;
    const left = bounds.left + (this.#panel.left + inX) * sx;
    const top = bounds.top + (this.#panel.top + inTop) * sy;
    const width = (this.#panel.w - inX * 2) * sx;
    const height = (this.#panel.h - inTop - inBottom) * sy;
    return { left, top, width, height };
  }

  #destroyDom() {
    this.#domEls.forEach((e) => e.remove());
    this.#domEls = [];
  }

  // ── formatting / lifecycle ──

  /** Compact UI-unit formatter (e.g. 1234.5 → "1,234.5"). */
  #fmt(n) {
    const v = Number(n) || 0;
    return v % 1 === 0 ? v.toLocaleString() : v.toLocaleString(undefined, { maximumFractionDigits: 2 });
  }
  #cap(s) {
    const str = String(s ?? '');
    return str.charAt(0).toUpperCase() + str.slice(1);
  }
  #esc(s) {
    return String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  }

  update() {
    super.update();
    if (this._controls?.wasBackKeyPressed?.()) {
      const overlay = this.#domEls.find((e) => e.style.zIndex === '120');
      if (overlay) {
        this.#closeOverlay(overlay);
        return;
      }
      this.#close();
    }
  }

  #close() {
    if (this.#closing) return;
    this.#closing = true;
    this.#destroyDom();
    const prev = this.#sceneData?.previousSceneName ?? SCENE_KEYS.WORLD_SCENE;
    this.cameras.main.fadeOut(150, 0, 0, 0);
    this.cameras.main.once(Phaser.Cameras.Scene2D.Events.FADE_OUT_COMPLETE, () => this.#finishClose(prev));
    this.time.delayedCall(200, () => this.#finishClose(prev));
  }

  #finishClose(prev) {
    if (!this.scene.isActive(SCENE_KEYS.VAULT_SCENE)) return;
    this.scene.stop(SCENE_KEYS.VAULT_SCENE);
    if (this.scene.isPaused(prev)) this.scene.resume(prev);
    else if (this.scene.isSleeping(prev)) {
      this.scene.wake(prev);
      this.scene.bringToTop(prev);
    } else if (!this.scene.isActive(prev)) this.scene.start(prev);
    const prevScene = this.scene.get(prev);
    if (prevScene?._controls) prevScene._controls.lockInput = false;
  }
}
